/**
 * Pure session model: what `api.fn_my_profile()` returns, and the questions the shell asks of
 * it. No React and no Supabase import, so every rule here is unit-testable.
 *
 * These checks decide what the UI OFFERS. They are never the control: every row and every RPC
 * is re-checked by RLS and the definer functions. A hidden menu item is a courtesy, not a guard.
 */

export type ProfileState = 'active' | 'invited' | 'suspended' | 'not_approved' | 'signed_out';

export type PermissionAction =
  | 'create' | 'read' | 'update' | 'delete' | 'submit' | 'approve'
  | 'export' | 'share' | 'configure' | 'reveal' | 'restore';

export type ScopeName = 'all' | 'team' | 'own' | 'none';

export interface SessionProfile {
  state: 'active';
  user: {
    id: string;
    full_name: string;
    user_type: string;
    preferred_language: string;
    mfa_required: boolean;
    team_id: string | null;
  };
  organization: {
    id: string; code: string; name: string; type: string;
    theme_color: string | null; default_language: string;
  } | null;
  /** Database ladder: 0 Super Admin ... 6 Viewer. Lower is more authority. Null = no role. */
  tier: number | null;
  roles: Array<{ id: string; code: string; name: string; tier: number }>;
  permissions: Record<string, PermissionAction[]>;
  record_scopes: Record<string, { record: ScopeName; picker: ScopeName }>;
  geography_scopes: Array<{ geography_id: string; name: string; type: string; mode: 'exact' | 'include_children' }>;
}

export type ProfileResult = SessionProfile | { state: Exclude<ProfileState, 'active'> };

/** Narrow whatever the RPC returned. Anything unrecognised is treated as not approved. */
export function parseProfile(raw: unknown): ProfileResult {
  if (!raw || typeof raw !== 'object') return { state: 'not_approved' };
  const state = (raw as { state?: unknown }).state;
  if (state === 'active') {
    const p = raw as Partial<SessionProfile>;
    if (!p.user?.id) return { state: 'not_approved' };
    return {
      state: 'active',
      user: p.user,
      organization: p.organization ?? null,
      tier: typeof p.tier === 'number' ? p.tier : null,
      roles: p.roles ?? [],
      permissions: p.permissions ?? {},
      record_scopes: p.record_scopes ?? {},
      geography_scopes: p.geography_scopes ?? [],
    };
  }
  if (state === 'invited' || state === 'suspended' || state === 'signed_out') return { state };
  return { state: 'not_approved' };
}

export function can(profile: SessionProfile | null | undefined, screenKey: string, action: PermissionAction = 'read'): boolean {
  return Boolean(profile?.permissions[screenKey]?.includes(action));
}

/** Tier 0/1: organisation administration. Mirrors the `<= 1` bypass in the RLS predicates. */
export function isAdmin(profile: SessionProfile | null | undefined): boolean {
  return profile?.tier != null && profile.tier <= 1;
}

export function scopeOf(profile: SessionProfile | null | undefined, screenKey: string): ScopeName {
  if (isAdmin(profile)) return 'all';
  return profile?.record_scopes[screenKey]?.record ?? 'all';
}

export const BLOCKED_MESSAGES: Record<Exclude<ProfileState, 'active' | 'signed_out'>, string> = {
  invited: 'Your invitation has not been activated yet. Ask your organisation admin to complete it.',
  suspended: 'Your access has been suspended. Contact your organisation admin.',
  not_approved: 'This sign-in is not approved for Vanam.S360.earth. Only users invited by an organisation admin can sign in.',
};

// ---------------------------------------------------------------------------------------
// Sign-in identifiers
// ---------------------------------------------------------------------------------------

export type LoginChannel = 'whatsapp' | 'sms' | 'email';

export const LOGIN_CHANNELS: Array<{ key: LoginChannel; label: string; hint: string }> = [
  { key: 'whatsapp', label: 'WhatsApp', hint: 'Code sent to your WhatsApp number' },
  { key: 'sms', label: 'SMS', hint: 'Code sent by text message' },
  { key: 'email', label: 'Email', hint: 'Code sent to your email' },
];

/**
 * Normalise what the user typed into what Auth stores. Phone numbers become E.164; a bare
 * 10-digit number is taken as Indian (+91), which is the deployment. Returns an error string
 * instead of throwing so the form can show it under the field.
 */
export function normaliseIdentifier(channel: LoginChannel, input: string): { value: string } | { error: string } {
  const raw = input.trim();
  if (!raw) return { error: channel === 'email' ? 'Enter your email address.' : 'Enter your mobile number.' };

  if (channel === 'email') {
    const email = raw.toLowerCase();
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email) ? { value: email } : { error: 'That does not look like an email address.' };
  }

  const digits = raw.replace(/[\s\-().]/g, '');
  let e164: string;
  if (digits.startsWith('+')) e164 = digits;
  else if (/^0?[6-9]\d{9}$/.test(digits)) e164 = `+91${digits.replace(/^0/, '')}`;
  else if (/^91[6-9]\d{9}$/.test(digits)) e164 = `+${digits}`;
  else return { error: 'Enter a 10-digit mobile number, or include the country code (+91...).' };

  return /^\+[1-9]\d{7,14}$/.test(e164) ? { value: e164 } : { error: 'That does not look like a valid mobile number.' };
}

/** "+919800000003" -> "+91 ******0003". The code screen echoes where the code went. */
export function maskIdentifier(channel: LoginChannel, value: string): string {
  if (channel === 'email') {
    const [name = '', domain = ''] = value.split('@');
    return `${name.slice(0, 1)}${'*'.repeat(Math.max(name.length - 1, 1))}@${domain}`;
  }
  return `${value.slice(0, 3)} ${'*'.repeat(Math.max(value.length - 7, 0))}${value.slice(-4)}`;
}
